import { ApolloError, useApolloClient } from '@apollo/client'
import { useLazyQuery } from '@apollo/client/react/hooks/useLazyQuery'
import { summarizeApolloError } from '@deltek/specification-client/core/Utilities/apolloUtils'
import { useCallback } from 'react'

import {
    GetFamilyElementChanges,
    GetProductTypeChanges,
} from 'api/graphQL/queries/changes.queries'
import { useElementItemShallowSelector } from 'components/WorkspaceEditor/ElementItemStore'
import { useNotification } from 'components/WorkspaceEditor/services/notificationService'
import { useSuggestionsShallowSelector } from 'components/WorkspaceEditor/SuggestionStore'
import { TrackedChange } from 'models/changes'
import { useParams } from 'react-router-dom'
import { useTrackChangesUpdateShallowSelector } from '../services'
import { normalizeTrackChanges } from '../utils/TrackChangesAndSuggestionsUtils'

interface TrackChangesQueryResponse {
    getProjectProductFamily?: {
        trackChanges: TrackedChange[]
    }
    getProjectNugget?: {
        trackChanges: TrackedChange[]
    }
}

interface GetTrackChangesQueryVariables {
    input: { projectId: string; elementId: string }
    includeProductTypeInFamilyTrackChanges?: boolean
}

/**
 * A custom hook that provides functionality to fetch and manage TrackChanges data.
 *
 * @returns {Object} An object containing the `fetchTrackChangesData` function.
 * @returns {Function} returns.fetchTrackChangesData - A function to fetch the project data.
 */
export const useTrackChangesQuery = (): {
    fetchTrackChangesData: (overrideElementId?: string) => Promise<void>
    fetchTrackChangesDataWithApolloClient: (
        overrideElementId?: string
    ) => Promise<void>
} => {
    const apolloClient = useApolloClient()
    const notification = useNotification()
    const { projectId = '' } = useParams<{ projectId: string }>()
    const {
        isProjectProductFamily,
        elementId,
        isProductTypeProject,
    } = useElementItemShallowSelector((s) => ({
        isProjectProductFamily: s.elementItemValue.isProjectProductFamily,
        elementId: s.elementItemValue.elementId,
        isProductTypeProject: s.elementItemValue.isProductTypeProject,
    }))
    const {
        setTrackChangesData,
        setIsSuggestionsDataUpdated,
        trackChangesData,
        isSuggestionsDataUpdated,
    } = useSuggestionsShallowSelector((s) => ({
        setTrackChangesData: s.setTrackChangesData,
        trackChangesData: s.trackChangesData,
        setIsSuggestionsDataUpdated: s.setIsSuggestionsDataUpdated,
        isSuggestionsDataUpdated: s.isSuggestionsDataUpdated,
    }))
    const { setIsTrackedChangesFetched } = useTrackChangesUpdateShallowSelector(
        (s) => ({
            setIsTrackedChangesFetched: s.setIsTrackedChangesFetched,
        })
    )
    const showError = (error: ApolloError) => {
        notification.error({ message: error.message })
        console.error(
            summarizeApolloError(error, 'WorkspaceEditor-getTrackChangesQuery')
        )
    }

    /**
     * Determines if track changes data should be updated based on the response
     * @returns true if data has changes and should be updated, false otherwise
     */
    const shouldUpdateTrackChanges = (
        trackChanges: TrackedChange[],
        normalizedData: {
            trackChangesDataWithId: Record<string, TrackedChange>
            removeTrackChangesId: string[]
        }
    ): boolean => {
        // No track changes from API
        if (!trackChanges || trackChanges.length === 0) {
            // Only clear if this is initial load (no existing data)
            return Object.keys(trackChangesData).length === 0
        }

        // Check if there are actual changes or removes
        return (
            Object.keys(normalizedData.trackChangesDataWithId).length > 0 ||
            normalizedData.removeTrackChangesId.length > 0
        )
    }

    const handleSuccess = useCallback(
        (response: TrackChangesQueryResponse | null) => {
            const trackChanges = (response?.getProjectProductFamily
                ?.trackChanges ??
                response?.getProjectNugget?.trackChanges) as TrackedChange[]

            // Handle empty response
            if (!trackChanges || trackChanges.length === 0) {
                // Initial load with no track changes - clear the store
                setTrackChangesData({}, [], true)
                return
            }

            const normalizedData = normalizeTrackChanges(
                trackChanges,
                trackChangesData
            )

            // Only update if there are actual changes
            if (shouldUpdateTrackChanges(trackChanges, normalizedData)) {
                setTrackChangesData(
                    normalizedData.trackChangesDataWithId,
                    normalizedData.removeTrackChangesId
                )
            }
        },
        [trackChangesData, setTrackChangesData]
    )

    const createTrackChangesQuery = (query: any, label: string) =>
        useLazyQuery(query, {
            displayName: label,
            fetchPolicy: 'no-cache',
            onCompleted: (data) => {
                handleSuccess(data)

                if (!isSuggestionsDataUpdated) {
                    setIsTrackedChangesFetched?.(true)
                }
                setIsSuggestionsDataUpdated(false)
            },
            onError: (error) => {
                showError(error)
                setIsSuggestionsDataUpdated(false)
                setIsTrackedChangesFetched?.(false)
            },
        })

    const [fetchFamilyChanges] = createTrackChangesQuery(
        GetFamilyElementChanges,
        'GetFamilyChanges'
    )
    const [fetchNuggetChanges] = createTrackChangesQuery(
        GetProductTypeChanges,
        'GetNuggetChanges'
    )

    const fetchTrackChangesData = useCallback(
        async (overrideElementId?: string) => {
            // Use the provided elementId if available, otherwise use the one from store
            const elementIdToUse = overrideElementId || elementId

            const variables: GetTrackChangesQueryVariables = {
                input: {
                    projectId,
                    elementId: elementIdToUse,
                },
                ...(isProductTypeProject
                    ? {}
                    : {
                          includeProductTypeInFamilyTrackChanges: isProjectProductFamily,
                      }),
            }

            const queryFn = isProductTypeProject
                ? fetchNuggetChanges
                : fetchFamilyChanges
            return queryFn({ variables })
        },
        [
            projectId,
            elementId,
            isProductTypeProject,
            isProjectProductFamily,
            isProductTypeProject,
            fetchFamilyChanges,
            fetchNuggetChanges,
        ]
    )

    /**
     * Fetches track changes data using Apollo client directly.
     * Preferred over fetchTrackChangesData for use in mutation callbacks where
     * synchronous execution is required (e.g., onCompleted callbacks).
     *
     * Benefits:
     * - Synchronous promise-based execution
     * - No dependency on React render cycles
     * - Reliable timing in mutation callbacks
     *
     * @param overrideElementId - Optional elementId to use instead of store value
     * @returns Promise that resolves when track changes data is fetched and processed
     */
    const fetchTrackChangesDataWithApolloClient = useCallback(
        async (overrideElementId?: string) => {
            const elementIdToUse = overrideElementId || elementId

            const variables: GetTrackChangesQueryVariables = {
                input: {
                    projectId,
                    elementId: elementIdToUse,
                },
                ...(isProductTypeProject
                    ? {}
                    : {
                          includeProductTypeInFamilyTrackChanges: isProjectProductFamily,
                      }),
            }

            const query = isProductTypeProject
                ? GetProductTypeChanges
                : GetFamilyElementChanges

            try {
                const result = await apolloClient.query({
                    query,
                    variables,
                    fetchPolicy: 'no-cache',
                })

                handleSuccess(result.data)

                if (!isSuggestionsDataUpdated) {
                    setIsTrackedChangesFetched?.(true)
                }
                setIsSuggestionsDataUpdated(false)
            } catch (error) {
                showError(error as ApolloError)
                setIsSuggestionsDataUpdated(false)
                setIsTrackedChangesFetched?.(false)
            }
        },
        [
            apolloClient,
            projectId,
            elementId,
            isProductTypeProject,
            isProjectProductFamily,
            handleSuccess,
            isSuggestionsDataUpdated,
            setIsTrackedChangesFetched,
            setIsSuggestionsDataUpdated,
        ]
    )

    return { fetchTrackChangesData, fetchTrackChangesDataWithApolloClient }
}
